import get from "lodash/get";

/**
 * Pure helper logic for the Path Schedule editor (Workstream 2).
 *
 * Responsibilities:
 *  - Resolve a selected system-generated configuration (settingId) to its paired
 *    FILL and PICK SortPathInfo entries.
 *  - Build the SetPathSchedule request body from modal form state.
 *  - Client-side validation and same-date conflict detection.
 *
 * No React, no network — kept pure so it can be unit tested in isolation (Workstream 4).
 */

/**
 * Modes the AddScheduleModal can open in. Each governs field editability and the
 * effective-date rule applied on save.
 */
export const SCHEDULE_MODAL_MODE = {
    CREATE: "CREATE",              // brand new staged entry
    EDIT_ACTIVE: "EDIT_ACTIVE",    // overwrite the active entry (effective date read-only)
    EDIT_STAGED: "EDIT_STAGED",    // edit a staged entry (effective date editable)
    ACTIVATE_NOW: "ACTIVATE_NOW"   // create a station-now entry from a staged config
};

export const SORT_PATH_TYPE = {
    FILL: "FILL",
    PICK: "PICK"
};

export const SORT_PATH_SOURCE = {
    SPG: "SPG",
    NARC: "NARC"
};

/**
 * Resolves the paired FILL and PICK SortPathInfo entries for a selected system-generated
 * configuration. System paths bind to settingId + cycle + type; NARC manual paths have no
 * settingId and are excluded from this pairing (they are chosen individually).
 *
 * @param settingId the selected SortPathSetting's settingId
 * @param cycle the selected cycle name
 * @param sortPathInfoList list of SortPathInfo from GetSortPathInfoList
 * @returns {{fillPathId: (string|undefined), pickPathId: (string|undefined)}}
 */
export function resolvePairedPaths(settingId, cycle, sortPathInfoList) {
    const list = Array.isArray(sortPathInfoList) ? sortPathInfoList : [];
    const matches = (info, type) =>
        get(info, ["settingId"]) === settingId
        && get(info, ["cycle"]) === cycle
        && get(info, ["type"]) === type;

    const fill = list.find((info) => matches(info, SORT_PATH_TYPE.FILL));
    const pick = list.find((info) => matches(info, SORT_PATH_TYPE.PICK));
    return {
        fillPathId: fill ? get(fill, ["sortPathId"]) : undefined,
        pickPathId: pick ? get(pick, ["sortPathId"]) : undefined
    };
}

/**
 * Returns the sort path entries eligible for selection in the Fill/Pick dropdowns:
 * system-generated (SPG) paths for the selected cycle plus all NARC manual paths
 * (which are cycle-agnostic). Filtered to the requested type (FILL/PICK).
 *
 * @param type FILL or PICK
 * @param cycle selected cycle name
 * @param sortPathInfoList list of SortPathInfo from GetSortPathInfoList
 * @returns {Array} filtered SortPathInfo entries
 */
export function eligiblePathsForType(type, cycle, sortPathInfoList) {
    const list = Array.isArray(sortPathInfoList) ? sortPathInfoList : [];
    return list.filter((info) => {
        if (get(info, ["type"]) !== type) {
            return false;
        }
        const source = get(info, ["source"]);
        if (source === SORT_PATH_SOURCE.NARC) {
            return true; // NARC paths are cycle-agnostic
        }
        // SPG paths: restrict to the selected cycle
        return get(info, ["cycle"]) === cycle;
    });
}

/**
 * Builds a form-state seed from an existing PathScheduleEntry, used to pre-fill the modal
 * for edit / activate-now flows.
 *
 * @param entry a PathScheduleEntry (active or staged)
 * @returns {{effectiveDate, fillPathId, pickPathId, dayOfWeekOverrides, version}}
 */
export function formStateFromEntry(entry) {
    return {
        effectiveDate: get(entry, ["effectiveDate"]),
        fillPathId: get(entry, ["defaultPathConfig", "fillPathId"]),
        pickPathId: get(entry, ["defaultPathConfig", "pickPathId"]),
        dayOfWeekOverrides: get(entry, ["dayOfWeekOverrides"]) || {},
        version: get(entry, ["version"])
    };
}

/**
 * Builds the SetPathSchedule request body arguments from modal form state.
 * dayOfWeekOverrides entries with no fill or pick are dropped.
 *
 * @param form { effectiveDate, fillPathId, pickPathId, dayOfWeekOverrides, version }
 * @returns {{effectiveDate, defaultPathConfig, dayOfWeekOverrides, version}}
 */
export function buildSetScheduleArgs(form) {
    const overridesInput = form.dayOfWeekOverrides || {};
    const dayOfWeekOverrides = {};
    Object.keys(overridesInput).forEach((day) => {
        const o = overridesInput[day];
        if (o && o.fillPathId && o.pickPathId) {
            dayOfWeekOverrides[day] = { fillPathId: o.fillPathId, pickPathId: o.pickPathId };
        }
    });

    return {
        effectiveDate: form.effectiveDate,
        defaultPathConfig: { fillPathId: form.fillPathId, pickPathId: form.pickPathId },
        dayOfWeekOverrides: Object.keys(dayOfWeekOverrides).length > 0 ? dayOfWeekOverrides : undefined,
        version: form.version
    };
}

/**
 * Returns true if the given effective date (date-only or date-time string) is strictly
 * in the future relative to the supplied station-local "now". Comparison is lexicographic
 * on normalized ISO strings, consistent with the backend's date handling.
 *
 * @param effectiveDate candidate effective date
 * @param stationNow station-local current date (or date-time) string
 */
export function isFutureDate(effectiveDate, stationNow) {
    if (!effectiveDate || !stationNow) {
        return false;
    }
    // Compare on the date portion when either side is date-only.
    const datePart = (s) => s.split("T")[0];
    return datePart(effectiveDate) > datePart(stationNow);
}

/**
 * Normalizes an effective-date value into the strict `YYYY-MM-DD` form that a native
 * `<input type="date">` requires. Anything the browser can't parse (a full ISO
 * timestamp, an epoch-millis number, a partial string) otherwise makes the date
 * picker open at a bogus year (e.g., year 0004). Returns "" when the value is
 * empty/unparseable so the input renders its placeholder and opens at "today".
 *
 * @param value date string (YYYY-MM-DD or ISO date-time), epoch millis, or nullish
 * @returns {string} a `YYYY-MM-DD` string, or "" if not derivable
 */
export function toDateInputValue(value) {
    if (value === undefined || value === null || value === "") {
        return "";
    }
    // Already a clean date-only string (optionally with a time component we can trim).
    if (typeof value === "string") {
        const datePart = value.split("T")[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
            return datePart;
        }
    }
    // Fall back to Date parsing for epoch millis or other parseable representations.
    const d = new Date(typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value);
    if (isNaN(d.getTime())) {
        return "";
    }
    // Use local date parts (not toISOString) to avoid a UTC day-shift.
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return yyyy + "-" + mm + "-" + dd;
}

/**
 * Client-side validation of the schedule form, per design Section 6.
 * Returns an array of error message-id descriptors ({ id, defaultMessage, values }).
 * An empty array means valid.
 *
 * @param form { effectiveDate, fillPathId, pickPathId, dayOfWeekOverrides }
 * @param mode one of SCHEDULE_MODAL_MODE
 * @param context { stationNow, validPathIds:Set }
 */
export function validateScheduleForm(form, mode, context) {
    const errors = [];
    const stationNow = get(context, ["stationNow"]);
    const validPathIds = get(context, ["validPathIds"]);

    // Effective date presence (not required for ACTIVATE_NOW — generated by UI)
    if (mode !== SCHEDULE_MODAL_MODE.ACTIVATE_NOW && !form.effectiveDate) {
        errors.push({ id: "pathSchedule.validation.effectiveDateRequired",
            defaultMessage: "Please select an effective date" });
    }

    // Future-date rule for new / rescheduled staged entries (not for ACTIVE update or ACTIVATE_NOW)
    if ((mode === SCHEDULE_MODAL_MODE.CREATE || mode === SCHEDULE_MODAL_MODE.EDIT_STAGED)
        && form.effectiveDate && stationNow && !isFutureDate(form.effectiveDate, stationNow)) {
        errors.push({ id: "pathSchedule.validation.effectiveDateFuture",
            defaultMessage: "Effective date must be a future date" });
    }

    // Fill / Pick required
    if (!form.fillPathId) {
        errors.push({ id: "pathSchedule.validation.fillPathRequired",
            defaultMessage: "Please select a Fill Path" });
    }
    if (!form.pickPathId) {
        errors.push({ id: "pathSchedule.validation.pickPathRequired",
            defaultMessage: "Please select a Pick Path" });
    }

    // Day-of-week override validity: each populated override must have both fill+pick,
    // and referenced paths must exist in the GetSortPathInfoList response (if provided).
    const overrides = form.dayOfWeekOverrides || {};
    Object.keys(overrides).forEach((day) => {
        const o = overrides[day] || {};
        const hasAny = o.fillPathId || o.pickPathId;
        if (hasAny && (!o.fillPathId || !o.pickPathId)) {
            errors.push({ id: "pathSchedule.validation.overrideIncomplete",
                defaultMessage: "Both Fill and Pick paths are required for the {day} override",
                values: { day } });
        }
        if (validPathIds && o.fillPathId && !validPathIds.has(o.fillPathId)) {
            errors.push({ id: "pathSchedule.validation.overrideInvalidPath",
                defaultMessage: "Invalid path selected for {day} override", values: { day } });
        }
        if (validPathIds && o.pickPathId && !validPathIds.has(o.pickPathId)) {
            errors.push({ id: "pathSchedule.validation.overrideInvalidPath",
                defaultMessage: "Invalid path selected for {day} override", values: { day } });
        }
    });

    return errors;
}

/**
 * Detects whether saving at the given effective date would overwrite an existing entry
 * in the current cycle's schedule state (active + staged), for the conflict warning.
 * Ignores the entry currently being edited (same effective date in EDIT flows is an
 * intentional overwrite, not a conflict to warn about).
 *
 * @param effectiveDate candidate effective date
 * @param activeSchedule active PathScheduleEntry or null
 * @param stagedSchedules array of staged PathScheduleEntry
 * @param editingEffectiveDate the effective date of the entry being edited (or null)
 * @returns {boolean} true if a different existing entry already occupies this date
 */
export function hasDateConflict(effectiveDate, activeSchedule, stagedSchedules, editingEffectiveDate) {
    if (!effectiveDate) {
        return false;
    }
    const datePart = (s) => (s ? s.split("T")[0] : s);
    const target = datePart(effectiveDate);
    if (editingEffectiveDate && datePart(editingEffectiveDate) === target) {
        return false; // overwriting the same entry being edited — not a conflict
    }
    const all = [];
    if (activeSchedule && get(activeSchedule, ["effectiveDate"])) {
        all.push(activeSchedule);
    }
    (Array.isArray(stagedSchedules) ? stagedSchedules : []).forEach((s) => all.push(s));
    return all.some((s) => datePart(get(s, ["effectiveDate"])) === target);
}

/**
 * Builds a Set of all valid selectable path IDs from the SortPathInfoList response,
 * used to validate day-of-week override selections.
 *
 * @param sortPathInfoList list of SortPathInfo
 * @returns {Set<string>}
 */
export function buildValidPathIdSet(sortPathInfoList) {
    const set = new Set();
    (Array.isArray(sortPathInfoList) ? sortPathInfoList : []).forEach((info) => {
        const id = get(info, ["sortPathId"]);
        if (id) {
            set.add(id);
        }
    });
    return set;
}
